import { useEffect, useState } from 'react'
import { DocsLayout } from '@/components/DocsLayout'
import { SearchBox } from '@/components/SearchBox'
import { fetchSearch, trackEvent } from '@/lib/api'
import { formatType, getCategoryLabel } from '@/lib/categories'
import { SITE_NAME } from '@/lib/config'
import { useHead } from '@/lib/head'
import { ArrowRightIcon, CaretRightIcon } from '@phosphor-icons/react'
import { Link, useSearchParams } from 'react-router-dom'

import type { PublicHelpSearchResult } from '@scrambl/shared/types/help-site'
import { productSearchLengthBucket } from '@scrambl/shared/product-analytics'

type SearchState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; results: PublicHelpSearchResult[] }
  | { status: 'error' }

export default function SearchPage() {
  const [params] = useSearchParams()
  const query = (params.get('q') ?? '').trim()
  const [state, setState] = useState<SearchState>({ status: 'idle' })

  useHead({
    title: query ? `“${query}” — ${SITE_NAME}` : `Search — ${SITE_NAME}`,
    description: 'Search the Scrambl Help Center.',
    path: '/search',
    noIndex: true,
  })

  useEffect(() => {
    if (!query) {
      setState({ status: 'idle' })
      return
    }
    let cancelled = false
    setState({ status: 'loading' })
    fetchSearch(query)
      .then(results => {
        if (cancelled) return
        setState({ status: 'ready', results })
        trackEvent({
          eventType: 'help_search_performed',
          queryLengthBucket: productSearchLengthBucket(query),
          resultCount: results.length,
        })
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [query])

  return (
    <DocsLayout>
      <div className="page">
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Help Center</Link>
          <CaretRightIcon size={11} />
          <span>Search</span>
        </nav>
        <h1 className="search-title">
          {query ? (
            <>
              Results for <em>“{query}”</em>
            </>
          ) : (
            'Search the Help Center'
          )}
        </h1>
        <div className="search-page-box">
          <SearchBox key={query} initialQuery={query} autoFocus={!query} />
        </div>

        {state.status === 'loading' ? (
          <div className="list-skeleton">
            {Array.from({ length: 5 }).map((_, position) => (
              <span key={position} />
            ))}
          </div>
        ) : null}

        {state.status === 'error' ? (
          <div className="notice notice-error">
            Search is unavailable right now. Try again in a moment.
          </div>
        ) : null}

        {state.status === 'ready' && state.results.length === 0 ? (
          <div className="notice">
            No articles matched. Try different words, or browse by product area
            from the sidebar.
          </div>
        ) : null}

        {state.status === 'ready' && state.results.length > 0 ? (
          <ol className="results">
            {state.results.map((result, position) => (
              <li
                key={result.id}
                className="result reveal"
                style={{ animationDelay: `${position * 40}ms` }}
              >
                <Link to={`/articles/${encodeURIComponent(result.id)}`}>
                  <span className="result-crumb">
                    {getCategoryLabel(result.moduleSlug)}
                    <CaretRightIcon size={10} />
                    {formatType(result.type)}
                  </span>
                  <span className="result-title">
                    {result.title} <ArrowRightIcon size={14} />
                  </span>
                  <span className="result-snippet">
                    {result.snippet || result.summary}
                  </span>
                </Link>
              </li>
            ))}
          </ol>
        ) : null}
      </div>
    </DocsLayout>
  )
}
